import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import {
  CircleHelp, Clock, Ellipsis, LayoutDashboard, LogOut, Monitor, Moon, PanelLeftClose, Pencil, Pin, PinOff,
  Repeat, Search, SquarePen, Sun, Trash, X,
} from 'lucide-react'
import type { GesprekMeta } from '../../gedeeld/types'
import { c, uitloggen } from '../lib/api'
import { groepeer, rollenKort, geleden, datumTijd } from '../lib/opmaak'
import { Link, navigeer, useRoute } from '../lib/router'
import {
  gesprekBijwerken, gesprekWeg, laadGesprekken, meldFout, useMag, useStaat, verversAi, zetLade, zetZijbalkDicht,
} from '../lib/staat'
import { kiesThema, THEMA_LABELS, useThema, type ThemaKeuze } from '../lib/thema'
import { nieuwGesprek } from './Shell'
import { Avatar, Logo, MenuRegel, Uitklap, vraagBevestiging } from './ui'

/* ===========================================================================
 *  De zijbalk
 *
 *  Bovenaan wat je het vaakst doet: een nieuw gesprek. Dan je gesprekken,
 *  in de groepen die iedereen van een chat-app kent (vastgezet, vandaag,
 *  gisteren, ...), met zoeken erboven. Onderaan hoe het met de pc op
 *  kantoor staat, en wie je bent.
 *
 *  Zoeken gaat naar de server (de actie gesprekken met q), want die zoekt
 *  ook in wat er in de gesprekken staat en niet alleen in de titel. Een
 *  kwart seconde na de laatste toets, zodat niet elke letter een vraag is.
 * =========================================================================== */

const SMAL = '(max-width: 900px)'

function useSmal(): boolean {
  return useSyncExternalStore(
    (l) => {
      const m = window.matchMedia(SMAL)
      m.addEventListener('change', l)
      return () => m.removeEventListener('change', l)
    },
    () => window.matchMedia(SMAL).matches,
  )
}

export default function Zijbalk() {
  const magTerugkerend = useMag('cowork.terugkerend')
  const dicht = useStaat((s) => s.zijbalkDicht)
  const lade = useStaat((s) => s.ladeOpen)
  const smal = useSmal()
  const nieuwKnop = useRef<HTMLButtonElement>(null)

  /* Een lade die dicht is, of een ingeklapte zijbalk, hoort niet in de
     tabvolgorde: wie met Tab door de pagina loopt, moet niet in iets
     onzichtbaars terechtkomen. */
  const weg = smal ? !lade : dicht

  useEffect(() => { if (smal && lade) nieuwKnop.current?.focus() }, [smal, lade])

  return (
    <aside className="zijbalk" aria-label="Gesprekken en navigatie" inert={weg}>
      <div className="zb-kop">
        <Link naar="/" className="zb-merk" aria-label="Cowork, nieuw gesprek">
          <Logo breedte={84} />
          <span className="zb-naam">Cowork</span>
        </Link>
        {smal
          ? <button type="button" className="kopbalk-knop" aria-label="Menu sluiten" onClick={() => zetLade(false)}><X size={18} /></button>
          : <button type="button" className="kopbalk-knop" aria-label="Zijbalk inklappen" title="Zijbalk inklappen"
            onClick={() => zetZijbalkDicht(true)}><PanelLeftClose size={18} /></button>}
      </div>

      <button ref={nieuwKnop} type="button" className="zb-nieuw" onClick={nieuwGesprek}>
        <SquarePen size={16} />
        <span>Nieuw gesprek</span>
        <kbd aria-hidden>Ctrl ⇧ O</kbd>
      </button>

      <Gesprekken />

      <nav className="zb-nav" aria-label="Meer van Cowork">
        {magTerugkerend && <NavLink naar="/taken" icoon={<Repeat size={16} />}>Terugkerende taken</NavLink>}
        <NavLink naar="/over" icoon={<CircleHelp size={16} />}>Wat kan Cowork?</NavLink>
      </nav>

      <div className="zb-voet">
        <AiStatusRegel />
        <Gebruiker />
      </div>
    </aside>
  )
}

function NavLink({ naar, icoon, children }: { naar: string; icoon: ReactNode; children: ReactNode }) {
  const { pad } = useRoute()
  const hier = pad === naar || pad.startsWith(naar + '/')
  return (
    <Link naar={naar} className={`zb-navlink ${hier ? 'actief' : ''}`} aria-current={hier ? 'page' : undefined}>
      {icoon}
      <span>{children}</span>
    </Link>
  )
}

/* ------------------------------------------------------------------ *
 *  De lijst
 * ------------------------------------------------------------------ */

function Gesprekken() {
  const gesprekken = useStaat((s) => s.gesprekken)
  const geladen = useStaat((s) => s.gesprekkenGeladen)
  const { delen } = useRoute()
  const actief = delen[0] === 'c' ? delen[1] ?? null : null

  const [q, setQ] = useState('')
  const [gevonden, setGevonden] = useState<GesprekMeta[] | null>(null)
  const [zoekt, setZoekt] = useState(false)

  useEffect(() => { void laadGesprekken() }, [])

  useEffect(() => {
    const term = q.trim()
    if (!term) { setGevonden(null); setZoekt(false); return }
    setZoekt(true)
    let weg = false
    const t = setTimeout(() => {
      c('gesprekken', { q: term })
        .then((a) => { if (!weg) setGevonden(a.gesprekken) })
        .catch((e) => { if (!weg) { setGevonden([]); meldFout(e) } })
        .finally(() => { if (!weg) setZoekt(false) })
    }, 250)
    return () => { weg = true; clearTimeout(t) }
  }, [q])

  /* Wat er in de lijst gebeurt (hernoemen, vastzetten) ook in de
     zoekresultaten laten zien, zonder opnieuw te zoeken. */
  const zoekLijst = useMemo(() => gevonden?.map((g) => gesprekken.find((x) => x.id === g.id) ?? g) ?? null, [gevonden, gesprekken])
  const uitZoek = (id: string) => setGevonden((x) => x?.filter((g) => g.id !== id) ?? null)

  const groepen = useMemo(() => groepeer(gesprekken), [gesprekken])

  return (
    <>
      <div className="zb-zoek">
        <Search size={15} aria-hidden />
        <input
          type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek in gesprekken"
          aria-label="Zoek in gesprekken"
          onKeyDown={(e) => { if (e.key === 'Escape' && q) { e.stopPropagation(); setQ('') } }}
        />
        {q && <button type="button" className="zb-zoek-wis" aria-label="Zoeken wissen" onClick={() => setQ('')}><X size={14} /></button>}
      </div>

      <div className="zb-lijst">
        {!geladen && <Skelet />}

        {geladen && zoekLijst === null && !q.trim() && (
          groepen.length ? groepen.map((g) => (
            <section key={g.titel} className="zb-groep" aria-label={g.titel}>
              <h2 className="zb-groep-titel">{g.titel}</h2>
              <ul>
                {g.gesprekken.map((x) => <GesprekRegel key={x.id} g={x} actief={x.id === actief} />)}
              </ul>
            </section>
          )) : (
            <p className="zb-leeg">Nog geen gesprekken. Wat je vraagt, komt hier te staan.</p>
          )
        )}

        {q.trim() && (
          <section className="zb-groep" aria-label="Zoekresultaten" aria-busy={zoekt}>
            <h2 className="zb-groep-titel">{zoekt ? 'Zoeken…' : 'Gevonden'}</h2>
            {zoekLijst && zoekLijst.length > 0 && (
              <ul>{zoekLijst.map((x) => <GesprekRegel key={x.id} g={x} actief={x.id === actief} onWeg={uitZoek} />)}</ul>
            )}
            {!zoekt && zoekLijst && !zoekLijst.length && <p className="zb-leeg">Niets gevonden voor “{q.trim()}”.</p>}
          </section>
        )}
      </div>
    </>
  )
}

function Skelet() {
  return (
    <div className="zb-skelet" aria-label="Gesprekken laden" role="status">
      <span className="skelet kort" />
      {[78, 64, 88, 52, 70, 60].map((b, i) => <span key={i} className="skelet" style={{ width: `${b}%` }} />)}
    </div>
  )
}

function GesprekRegel({ g, actief, onWeg }: { g: GesprekMeta; actief: boolean; onWeg?: (id: string) => void }) {
  const [menu, setMenu] = useState(false)
  const [hernoem, setHernoem] = useState(false)
  const [titel, setTitel] = useState(g.titel)
  const veld = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (hernoem) { setTitel(g.titel); requestAnimationFrame(() => { veld.current?.focus(); veld.current?.select() }) }
  }, [hernoem, g.titel])

  async function bewaarTitel() {
    const t = titel.trim()
    setHernoem(false)
    if (!t || t === g.titel) return
    /* Meteen in de lijst; lukt het niet, dan terug en een melding. */
    gesprekBijwerken({ ...g, titel: t })
    try {
      const { gesprek } = await c('gesprek-hernoemen', { id: g.id, titel: t })
      gesprekBijwerken(gesprek)
    } catch (e) {
      gesprekBijwerken(g)
      meldFout(e)
    }
  }

  async function vastzetten() {
    setMenu(false)
    gesprekBijwerken({ ...g, vastgezet: !g.vastgezet })
    try {
      const { gesprek } = await c('gesprek-vastzetten', { id: g.id, vast: !g.vastgezet })
      gesprekBijwerken(gesprek)
    } catch (e) {
      gesprekBijwerken(g)
      meldFout(e)
    }
  }

  async function verwijderen() {
    setMenu(false)
    const ja = await vraagBevestiging(`Het gesprek “${g.titel}” verdwijnt, met alles wat erin staat. Dat kan niet terug.`, 'Verwijderen')
    if (!ja) return
    try {
      await c('gesprek-weg', { id: g.id })
      gesprekWeg(g.id)
      onWeg?.(g.id)
      if (actief) navigeer('/', true)
    } catch (e) {
      meldFout(e)
    }
  }

  if (hernoem) {
    return (
      <li className="zb-item hernoemt">
        <input
          ref={veld} className="zb-hernoem" value={titel} maxLength={120} aria-label="Nieuwe titel"
          onChange={(e) => setTitel(e.target.value)}
          onBlur={() => void bewaarTitel()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); void bewaarTitel() }
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setHernoem(false) }
          }}
        />
      </li>
    )
  }

  return (
    <li className={`zb-item ${actief ? 'actief' : ''} ${menu ? 'menu-open' : ''}`}>
      <Link naar={`/c/${g.id}`} className="zb-link" aria-current={actief ? 'page' : undefined}
        title={`${g.titel}\n${g.laatste ? g.laatste + '\n' : ''}Bijgewerkt ${datumTijd(g.bijgewerkt)}`}>
        {g.taakId && <Clock size={13} className="zb-klok" aria-label="Terugkerende taak" />}
        <span className="zb-titel">{g.titel || 'Zonder titel'}</span>
      </Link>
      <Uitklap open={menu} onOpen={setMenu} breed={190} ankerKlasse="zb-meer-anker"
        knop={
          <button type="button" className="zb-meer" aria-label={`Opties voor ${g.titel}`} aria-haspopup="menu" aria-expanded={menu}>
            <Ellipsis size={16} />
          </button>
        }>
        <MenuRegel icoon={<Pencil size={14} />} onClick={() => { setMenu(false); setHernoem(true) }}>Hernoemen</MenuRegel>
        <MenuRegel icoon={g.vastgezet ? <PinOff size={14} /> : <Pin size={14} />} onClick={() => void vastzetten()}>
          {g.vastgezet ? 'Losmaken' : 'Vastzetten'}
        </MenuRegel>
        <div className="menu-scheiding" />
        <MenuRegel gevaar icoon={<Trash size={14} />} onClick={() => void verwijderen()}>Verwijderen</MenuRegel>
        <p className="zb-menu-voet">Bijgewerkt {geleden(g.bijgewerkt)}</p>
      </Uitklap>
    </li>
  )
}

/* ------------------------------------------------------------------ *
 *  De pc op kantoor
 * ------------------------------------------------------------------ */

function AiStatusRegel() {
  const ai = useStaat((s) => s.ai)

  /* Elke minuut, en meteen als het tabblad weer in beeld komt: wie na de
     lunch terugkomt, wil niet een minuut naar een oude stand kijken. */
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) void verversAi() }, 60000)
    const zicht = () => { if (!document.hidden) void verversAi() }
    document.addEventListener('visibilitychange', zicht)
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', zicht) }
  }, [])

  if (!ai) {
    return <div className="zb-ai"><span className="ai-bol" aria-hidden /><span>Eigen AI: even kijken…</span></div>
  }

  const extra = [
    ai.bezig ? `Nu bezig: ${ai.bezig}` : null,
    ai.wachtrij > 0 ? `${ai.wachtrij} ${ai.wachtrij === 1 ? 'vraag' : 'vragen'} in de wachtrij` : null,
    ai.laatst ? `Laatst gemeld ${geleden(ai.laatst)}` : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className={`zb-ai ${ai.online ? 'aan' : 'uit'}`} title={extra || undefined} role="status">
      <span className="ai-bol" aria-hidden />
      <span className="zb-ai-tekst">
        <span>{ai.online ? 'Eigen AI staat aan' : 'Pc op kantoor staat uit'}</span>
        {ai.online && (
          <small>
            <span className="mono">{ai.model}</span>
            {ai.wachtrij > 0 ? ` · ${ai.wachtrij} in de wachtrij` : ' · op kantoor'}
          </small>
        )}
        {!ai.online && <small>Vragen blijven een uur in de wachtrij</small>}
      </span>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Wie je bent
 * ------------------------------------------------------------------ */

const THEMA_ICOON: Record<ThemaKeuze, ReactNode> = {
  systeem: <Monitor size={14} />, licht: <Sun size={14} />, donker: <Moon size={14} />,
}

function Gebruiker() {
  const ik = useStaat((s) => s.ik)!
  const { keuze } = useThema()
  const [open, setOpen] = useState(false)

  return (
    <Uitklap open={open} onOpen={setOpen} breed={256} ankerKlasse="zb-ik-anker"
      knop={
        <button type="button" className={`zb-ik ${open ? 'open' : ''}`} aria-haspopup="menu" aria-expanded={open}
          aria-label={`Jouw menu: ${ik.naam}`}>
          <Avatar naam={ik.naam} id={ik.id} grootte={30} />
          <span className="zb-ik-tekst">
            <strong>{ik.naam}</strong>
            <small>{rollenKort(ik.rollen) || ik.email}</small>
          </span>
          <Ellipsis size={16} className="zb-ik-pijl" />
        </button>
      }>
      <div className="menu-persoon-kop">
        <strong>{ik.naam}</strong>
        <span>{ik.email}</span>
      </div>
      <div className="menu-scheiding" />
      <div className="menu-groep-titel">Weergave</div>
      {(['systeem', 'licht', 'donker'] as ThemaKeuze[]).map((k) => (
        <MenuRegel key={k} icoon={THEMA_ICOON[k]} actief={keuze === k} onClick={() => kiesThema(k)}>{THEMA_LABELS[k]}</MenuRegel>
      ))}
      <div className="menu-scheiding" />
      <MenuRegel icoon={<LayoutDashboard size={14} />}
        onClick={() => { setOpen(false); window.open('https://truckwash-workspace.com/', '_blank', 'noopener,noreferrer') }}>
        Naar het dashboard
      </MenuRegel>
      <MenuRegel gevaar icoon={<LogOut size={14} />} onClick={() => { void uitloggen().then(() => location.assign('/')) }}>
        Uitloggen
      </MenuRegel>
    </Uitklap>
  )
}
